import { useState, useRef, useEffect, type ReactNode, type CSSProperties } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
}

export function Tooltip({ children, content, position = "top" }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), 200);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  // Calculate position when tooltip becomes visible
  useEffect(() => {
    if (!isVisible || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const offset = 8;
    const padding = 12; // Minimum distance from viewport edge

    // Create a temporary element to measure tooltip size. Only measures the
    // text-only case; for ReactNode content, fall back to a default size.
    let tooltipWidth = 200;
    let tooltipHeight = 32;
    if (typeof content === "string") {
      const measureEl = document.createElement("div");
      measureEl.style.cssText = "position:fixed;visibility:hidden;white-space:nowrap;padding:6px 10px;font-size:12px;";
      measureEl.textContent = content;
      document.body.appendChild(measureEl);
      tooltipWidth = measureEl.offsetWidth;
      tooltipHeight = measureEl.offsetHeight;
      document.body.removeChild(measureEl);
    }

    let left = 0;
    let top = 0;

    switch (position) {
      case "top":
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        top = rect.top - offset - tooltipHeight;
        break;
      case "bottom":
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        top = rect.bottom + offset;
        break;
      case "left":
        left = rect.left - offset - tooltipWidth;
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        break;
      case "right":
        left = rect.right + offset;
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        break;
    }

    // Clamp to viewport bounds
    const maxLeft = window.innerWidth - tooltipWidth - padding;
    const maxTop = window.innerHeight - tooltipHeight - padding;
    left = Math.max(padding, Math.min(left, maxLeft));
    top = Math.max(padding, Math.min(top, maxTop));

    setTooltipStyle({ left, top });
  }, [isVisible, position, content]);

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-flex"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            style={{
              position: "fixed",
              zIndex: 9999,
              left: tooltipStyle.left,
              top: tooltipStyle.top,
            }}
            className="px-2.5 py-1.5 text-xs font-medium whitespace-nowrap bg-[var(--color-elevated)] text-[var(--color-text)] border border-[var(--color-border)] rounded-md shadow-lg pointer-events-none"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}
